import axios from 'axios'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AgentVerificationAssociationCheckType } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { useMutation, useQuery, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { APIErrorHandler } from '@percent/admin-dashboard/common/library/APIErrorHandler'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { AddNewProgram } from './AddNewProgram'
import { AddNewProgramFormModel } from './AddNewProgram.types'

const ASSOCIATION_CHECK_PRIORITY: Record<AgentVerificationAssociationCheckType, number> = {
  defer_to_partner: 1, // Should always run first
  external_agent_verification: 2, // Before domain_matching, but might be removed soon
  trusted_school_domain: 3, // Before domain_matching
  domain_matching: 4,
  association_emails: 5, // Should always be directly after domain_matching
  automatic_document_review: 6,
  manual: 7 // Should always run last
}

const sortAssociationChecksByPriority = <T extends { type: AgentVerificationAssociationCheckType }>(checks: T[]): T[] =>
  [...checks].sort((a, b) => ASSOCIATION_CHECK_PRIORITY[a.type] - ASSOCIATION_CHECK_PRIORITY[b.type])

export function AddNewProgramContainer() {
  const { validationSubmissionsService } = useServices()
  const { id, configurationId } = useRequiredParams()
  const toast = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const validationSubmissionId = useRef('')

  const [{ data: programData, isLoading: isLoadingProgram }] = useQuery(
    validationSubmissionsService.getValidationSubmissionConfiguration,
    { id: configurationId },
    { skip: !configurationId }
  )

  const [{ isLoading: isPatchingValidationSubmission }, { apiFunc: patchValidationSubmission }] = useMutation(
    validationSubmissionsService.patchValidationSubmission,
    response => {
      toast(t(configurationId ? 'toast.duplicateProgramSuccess' : 'toast.createProgramSuccess'), 'success')
      const isEligibility = response.data.data.configuration.checks.some(check => check.type === 'eligibility')

      if (isEligibility) {
        navigate(
          configurationId
            ? `/partners/${id}/programs/${response.data.data.id}/duplicate-eligibility`
            : `/partners/${id}/programs/${response.data.data.id}/eligibility`,
          { replace: true }
        )
      } else {
        navigate(-1)
      }
    },
    error => {
      if (axios.isAxiosError(error)) {
        toast(APIErrorHandler(error?.response?.data?.error), 'error')
      }
    }
  )

  const [{ isLoading: isCreatingValidationSubmission }, { apiFunc: createValidationSubmission }] = useMutation(
    validationSubmissionsService.createValidationSubmission,
    response => {
      validationSubmissionId.current = response.data.data.id
    },
    error => {
      if (axios.isAxiosError(error)) {
        toast(APIErrorHandler(error?.response?.data?.error), 'error')
      }
    }
  )

  const handleAddNewProgram = async (formValue: AddNewProgramFormModel) => {
    const filteredPartnerFields = formValue.partnerFields?.filter(field => field.id !== '')

    if (!configurationId) {
      await createValidationSubmission({ partnerId: id })
    }

    await patchValidationSubmission({
      id: configurationId ?? validationSubmissionId.current,
      name: formValue.programName || null,
      primaryColor: formValue.primaryColor || null,
      shouldSendOutcomeEmail: formValue.shouldSendOutcomeEmails,
      acceptanceEmailBody: formValue.acceptanceEmailCopy || null,
      rejectionEmailBody: formValue.rejectionEmailCopy || null,
      programWebsite: formValue.programWebsite || null,
      partnerFields: filteredPartnerFields?.length ? filteredPartnerFields : undefined,
      configuration: {
        allowedOrganisations: {
          types: formValue.allowedOrganisationTypes
        },
        disallowedOrganisations: {
          relationships: formValue.disallowedOrganisations ?? []
        },
        checks: [
          ...(formValue.isEligibility ? [{ type: 'eligibility' as const }] : []),
          ...(formValue.validationRequestCheck ? [formValue.validationRequestCheck] : []),
          ...(formValue.flags?.length || formValue.failingFlags?.length
            ? [
                {
                  type: 'compliance' as const,
                  flags: formValue.flags ?? [],
                  failingFlags: formValue.failingFlags ?? []
                }
              ]
            : []),
          ...(formValue.identityChecks?.length || formValue.associationChecks?.length
            ? [
                {
                  type: 'agent_verification' as const,
                  identityChecks: formValue.identityChecks ?? [],
                  associationChecks: sortAssociationChecksByPriority(formValue.associationChecks ?? []),
                  isStatusDynamic: formValue.isStatusDynamic
                }
              ]
            : []),
          // Sent whole, not rebuilt: it carries the opt-ins and thresholds this form never shows.
          ...(formValue.fraudCheck ? [formValue.fraudCheck] : [])
        ],
        isStatusDynamic: formValue.isDynamicValidationSubmissionStatus
      }
    })
  }

  return (
    <AddNewProgram
      onAddNewProgram={handleAddNewProgram}
      isSaving={isCreatingValidationSubmission || isPatchingValidationSubmission}
      programData={programData}
      isLoadingProgram={isLoadingProgram}
    />
  )
}
